/**
 * What a piece stands on (FND-08) and how its outer sides are made (FND-13), shared by every
 * family.
 *
 * `base` is one of
 * - `none`: the bottom (or the sides) stand on the floor (every existing design, every golden);
 * - `plinth {height, setback}`: a kick board under the bottom, set back from the front edge;
 * - `feet {height}`: the whole piece lifted onto the kit's levelling feet (`hw.levelling-foot`,
 *   M8 with a 30 mm pad, 20 mm high, adjustable ±10 mm);
 * - `sides {height}`: the outer sides run down past the bottom to the floor and the bottom is
 *   raised `height` above it, with nothing between (integral feet, often with a shaped cut-out;
 *   pilot-v1-r2 p01). Case families only: the staggered stack has no full-height sides.
 *
 * In `design.params` the base is kept flat, so the generic Shape controls can edit it:
 * `base: 'none' | 'plinth' | 'feet' | 'sides'`, `plinth` (plinth height, ticks; the field cube and offset
 * grid always had), `plinthSetback` (ticks), `feetHeight` (ticks) and `toeHeight` (ticks, the gap
 * under a raised bottom). A design without `base`
 * reads as a plinth when `plinth > 0`, else none, so older designs keep their meaning.
 *
 * `sideStyle` is `panel` (default) or `slatted`: each outer side becomes vertical slats
 * `slatWidth` wide with `slatGap` between them, front and back slats flush with the edges.
 */
import type { Hardware } from './assembly';
import type { ParameterDef } from './types';
import { mm } from '../domain/units';

export type BaseKind = 'none' | 'plinth' | 'feet' | 'sides';
export type BaseSpec = { kind: 'none' } | { kind: 'plinth'; height: number; setback: number } | { kind: 'feet'; height: number } | { kind: 'sides'; height: number };

/**
 * Feet (kit v1.1.0): `levelling-foot` (M8, 30 mm pad, 20 mm high, adjustable 10–30 mm) under ply,
 * `turned-foot` (turned solid timber, 48 mm collar, 100 mm tall, the piece's own species) under
 * solid timber. Hardware is never scaled, so a turned foot is always 100 mm.
 */
export type FootKind = 'levelling-foot' | 'turned-foot';
export const FOOT: Readonly<Record<FootKind, { min: number; max: number; default: number; spec: string; label: string }>> = Object.freeze({
  'levelling-foot': { min: mm(10), max: mm(30), default: mm(20), spec: 'Levelling foot M8, 30 mm black nylon pad, 20 mm high (adjustable 10–30 mm)', label: 'Levelling feet' },
  'turned-foot': { min: mm(100), max: mm(100), default: mm(100), spec: 'Turned solid-timber foot, 48 mm collar, 100 mm tall, in the piece\'s own species; M8 hanger bolt', label: 'Turned feet' },
});
/** The foot for a stock: turned solid feet under solid timber, levelling feet under ply. */
export const footKindFor = (stockKind: 'sheet' | 'board'): FootKind => (stockKind === 'board' ? 'turned-foot' : 'levelling-foot');
/** Kept for callers that only need the levelling foot's range. */
export const FEET = FOOT['levelling-foot'];
export const PLINTH = Object.freeze({ min: mm(30), max: mm(150), default: mm(60), maxSetback: mm(100) });
/** Sides to the floor: the gap under the raised bottom. */
export const TOE = Object.freeze({ min: mm(20), max: mm(250), default: mm(60) });

export interface BaseParams {
  base?: BaseKind;
  plinth?: number;
  plinthSetback?: number;
  feetHeight?: number;
  toeHeight?: number;
}

/** The normalised base of a parameter block (see the module comment for the flat keys). */
export function baseOf(p: BaseParams | null | undefined, foot: FootKind = 'levelling-foot'): BaseSpec {
  const q = p ?? {};
  if (q.base === 'feet') return { kind: 'feet', height: q.feetHeight ?? FOOT[foot].default };
  if (q.base === 'sides') return { kind: 'sides', height: q.toeHeight ?? TOE.default };
  // a plinth height always means a plinth (older designs carry only `plinth`; `setBaseParam` keeps the two in step)
  const h = q.plinth ?? 0;
  if (h > 0 || q.base === 'plinth') return { kind: 'plinth', height: h > 0 ? h : PLINTH.default, setback: q.plinthSetback ?? 0 };
  return { kind: 'none' };
}

/** Height the carcass bottom is lifted by (plinth, feet or sides to the floor), ticks. */
export const liftOf = (b: BaseSpec): number => (b.kind === 'none' ? 0 : b.height);

/** Range errors for a base (field names are the flat keys). */
export function baseErrors(b: BaseSpec, D: number, foot: FootKind = 'levelling-foot'): { field: string; message: string; fix?: { label: string; patch: Record<string, unknown> } }[] {
  const out: { field: string; message: string; fix?: { label: string; patch: Record<string, unknown> } }[] = [];
  const clampFix = (key: string, v: number, lo: number, hi: number) => {
    const c = Math.min(hi, Math.max(lo, v));
    return { label: `Use ${c / 10} mm`, patch: { [key]: c } };
  };
  const F = FOOT[foot];
  if (b.kind === 'feet' && !(b.height >= F.min && b.height <= F.max)) {
    out.push({ field: 'feetHeight', message: F.min === F.max ? `${F.label} are ${F.min / 10} mm high.` : `${F.label} stand ${F.min / 10}–${F.max / 10} mm high.`, fix: clampFix('feetHeight', b.height, F.min, F.max) });
  }
  if (b.kind === 'sides' && !(b.height >= TOE.min && b.height <= TOE.max)) {
    out.push({ field: 'toeHeight', message: `The gap under the bottom must be ${TOE.min / 10}–${TOE.max / 10} mm.`, fix: clampFix('toeHeight', b.height, TOE.min, TOE.max) });
  }
  if (b.kind === 'plinth') {
    if (!(b.height >= PLINTH.min && b.height <= PLINTH.max)) out.push({ field: 'plinth', message: `The plinth must be ${PLINTH.min / 10}–${PLINTH.max / 10} mm high.`, fix: clampFix('plinth', b.height, PLINTH.min, PLINTH.max) });
    const maxSet = Math.min(PLINTH.maxSetback, Math.max(0, D - mm(100)));
    if (!(b.setback >= 0 && b.setback <= maxSet)) out.push({ field: 'plinthSetback', message: `The plinth can be set back at most ${maxSet / 10} mm.`, fix: clampFix('plinthSetback', b.setback, 0, maxSet) });
  }
  return out;
}

/**
 * Levelling feet under a carcass: a foot 40 mm in from each corner of every part that stands
 * on the base (front and back), plus a middle pair under long parts (over 1200 mm). Hardware
 * points are millimetres on the part's underside (y = lift).
 */
export function feetHardware(lift: number, under: readonly { id: string; x0: number; x1: number; z0: number; z1: number }[], foot: FootKind = 'levelling-foot'): Hardware[] {
  const at: Hardware['at'] = [];
  const inset = foot === 'turned-foot' ? 50 : 40;
  for (const u of under) {
    const x0 = u.x0 / 10, x1 = u.x1 / 10, z0 = u.z0 / 10, z1 = u.z1 / 10;
    const xs = x1 - x0 <= 2 * inset ? [(x0 + x1) / 2] : [x0 + inset, x1 - inset];
    if (x1 - x0 > 1200) xs.splice(1, 0, (x0 + x1) / 2);
    const zs = z1 - z0 <= 2 * inset ? [(z0 + z1) / 2] : [z0 + inset, z1 - inset];
    for (const x of xs) for (const z of zs) at.push({ partId: u.id, x, y: lift / 10, z });
  }
  return at.length ? [{ kind: foot, qty: at.length, at, visible: true, spec: FOOT[foot].spec }] : [];
}

/** The parts feet go under in a case: each side, or its front and back stiles when slatted. */
export function feetSupports(boards: readonly { id: string; box: { x: number; z: number; sx: number; sz: number } }[]): { id: string; x0: number; x1: number; z0: number; z1: number }[] {
  const out: (typeof boards)[number][] = [];
  for (const side of ['side-l', 'side-r']) {
    const own = boards.filter((q) => q.id === side || q.id.startsWith(`${side}-stile-`)).slice().sort((a, b) => a.box.z - b.box.z);
    if (own.length === 1) out.push(own[0]!);
    else if (own.length > 1) out.push(own[0]!, own[own.length - 1]!);
  }
  return out.map((q) => ({ id: q.id, x0: q.box.x, x1: q.box.x + q.box.sx, z0: q.box.z, z1: q.box.z + q.box.sz }));
}

// ---------------------------------------------------------------------------------------------
// Slatted sides (FND-13)
// ---------------------------------------------------------------------------------------------

export type SideStyle = 'panel' | 'slatted';
export const SLATS = Object.freeze({ widthMin: mm(25), widthMax: mm(150), widthDefault: mm(60), gapMin: mm(10), gapMax: mm(120), gapDefault: mm(30) });

export interface SideParams {
  sideStyle?: SideStyle;
  slatWidth?: number;
  slatGap?: number;
}

export interface SlatSpec { width: number; gap: number }

/** The slats of a slatted side, or null for plain panels. */
export function slatsOf(p: SideParams | null | undefined): SlatSpec | null {
  if (p?.sideStyle !== 'slatted') return null;
  return { width: p.slatWidth ?? SLATS.widthDefault, gap: p.slatGap ?? SLATS.gapDefault };
}

/**
 * Slats across a space (ticks), exactly as the kit lays them out (src/viewer/kit/slats.ts
 * `slatLayout`): every slat exactly `width` wide, exactly `gap` between slats, as many as fit
 * with at least `gap` at both ends; the leftover is shared by the two end gaps, never by
 * stretching a slat or a gap. The count follows the space.
 */
export function slatLayout(z0: number, space: number, s: SlatSpec): { z0: number; z1: number }[] {
  if (!(s.width > 0) || !(s.gap >= 0) || space < s.width) return [];
  const count = Math.max(1, Math.floor((space - s.gap) / (s.width + s.gap)));
  const used = count * s.width + (count - 1) * s.gap;
  const margin = Math.floor((space - used) / 2);
  return Array.from({ length: count }, (_x, i) => ({ z0: z0 + margin + i * (s.width + s.gap), z1: z0 + margin + i * (s.width + s.gap) + s.width }));
}

export function slatErrors(s: SlatSpec | null, depth: number): { field: string; message: string; fix?: { label: string; patch: Record<string, unknown> } }[] {
  if (!s) return [];
  const out: { field: string; message: string; fix?: { label: string; patch: Record<string, unknown> } }[] = [];
  if (!(s.width >= SLATS.widthMin && s.width <= SLATS.widthMax)) out.push({ field: 'slatWidth', message: `Slats must be ${SLATS.widthMin / 10}–${SLATS.widthMax / 10} mm wide.` });
  if (!(s.gap >= SLATS.gapMin && s.gap <= SLATS.gapMax)) out.push({ field: 'slatGap', message: `Gaps between slats must be ${SLATS.gapMin / 10}–${SLATS.gapMax / 10} mm.` });
  // the side is a frame: front and back stiles (one slat wide), rails, and at least one slat between
  if (3 * s.width + 2 * s.gap > depth) out.push({ field: 'slatWidth', message: `${s.width / 10} mm slats with ${s.gap / 10} mm gaps do not fit a ${depth / 10} mm deep side (two stiles and a slat).`, fix: { label: 'Use 30 mm slats', patch: { slatWidth: mm(30), slatGap: mm(15) } } });
  return out;
}

// ---------------------------------------------------------------------------------------------
// Shape controls and setParam behaviour shared by the families that keep `design.params`
// ---------------------------------------------------------------------------------------------


/** The keys `setBaseParam` owns. */
export const BASE_KEYS = ['base', 'plinth', 'plinthSetback', 'feetHeight', 'toeHeight'] as const;
export const SIDE_KEYS = ['sideStyle', 'slatWidth', 'slatGap'] as const;

/**
 * Shape controls for the base (generic: enum + lengths with showWhen). `sides` (sides to the
 * floor) is offered only by families with full-height outer sides.
 */
export function baseParameterDefs(group = 'Base', opts: { sides?: boolean } = { sides: true }): ParameterDef[] {
  return [
    {
      key: 'base', type: 'enum', default: 'none', critical: false,
      options: [
        { value: 'none', label: 'On the floor', help: 'The bottom stands on the floor.' },
        { value: 'plinth', label: 'Plinth', help: 'A kick board under the bottom, flush or set back.' },
        { value: 'feet', label: 'Feet', help: 'Separate feet under each corner: levelling feet under plywood, turned feet under solid timber.' },
        ...(opts.sides ? [{ value: 'sides', label: 'Sides to the floor', help: 'The sides run down past the bottom and stand on the floor as feet; the bottom is raised with an open gap under it.' }] : []),
      ],
      ui: { label: 'Stands on', control: 'choice', group },
    },
    { key: 'plinth', type: 'length', range: { min: PLINTH.min, max: PLINTH.max, step: 10 }, default: PLINTH.default, unit: 'mm', critical: false, ui: { label: 'Plinth height', control: 'field', group, showWhen: { key: 'base', equals: 'plinth' } } },
    { key: 'plinthSetback', type: 'length', range: { min: 0, max: PLINTH.maxSetback, step: 10 }, default: 0, unit: 'mm', critical: false, ui: { label: 'Plinth set back', help: 'How far the kick board sits behind the front edge.', control: 'field', group, showWhen: { key: 'base', equals: 'plinth' } } },
    ...(opts.sides ? [{ key: 'toeHeight', type: 'length', range: { min: TOE.min, max: TOE.max, step: 10 }, default: TOE.default, unit: 'mm', critical: false, ui: { label: 'Gap under the bottom', help: 'How high the bottom sits above the floor between the sides.', control: 'field', group, showWhen: { key: 'base', equals: 'sides' } } } as ParameterDef] : []),
    { key: 'feetHeight', type: 'length', range: { min: FOOT['levelling-foot'].min, max: FOOT['turned-foot'].max, step: 10 }, default: FOOT['levelling-foot'].default, unit: 'mm', critical: false, ui: { label: 'Feet height', help: 'Levelling feet (ply) adjust 10–30 mm; turned solid feet (solid timber) are 100 mm.', control: 'field', group, showWhen: { key: 'base', equals: 'feet' } } },
  ];
}

export function sideParameterDefs(group = 'Construction'): ParameterDef[] {
  return [
    {
      key: 'sideStyle', type: 'enum', default: 'panel', critical: false,
      options: [
        { value: 'panel', label: 'Panels', help: 'Each outer side is one board (glued up when wide).' },
        { value: 'slatted', label: 'Slats', help: 'Each outer side is a row of vertical slats with gaps between them.' },
      ],
      ui: { label: 'Sides', control: 'choice', group },
    },
    { key: 'slatWidth', type: 'length', range: { min: SLATS.widthMin, max: SLATS.widthMax, step: 10 }, default: SLATS.widthDefault, unit: 'mm', critical: false, ui: { label: 'Slat width', control: 'field', group, showWhen: { key: 'sideStyle', equals: 'slatted' } } },
    { key: 'slatGap', type: 'length', range: { min: SLATS.gapMin, max: SLATS.gapMax, step: 10 }, default: SLATS.gapDefault, unit: 'mm', critical: false, ui: { label: 'Gap between slats', control: 'field', group, showWhen: { key: 'sideStyle', equals: 'slatted' } } },
  ];
}

/**
 * Set a base or side key in a flat parameter block (null when `key` is not one of them).
 * Choosing a plinth with no height gives it the default height; leaving it clears the height,
 * so `baseOf` and the old `plinth`-only reading always agree.
 */
export function setBaseParam<P extends BaseParams & SideParams>(p: P, key: string, value: unknown): P | null {
  if (!(BASE_KEYS as readonly string[]).includes(key) && !(SIDE_KEYS as readonly string[]).includes(key)) return null;
  const q = { ...p } as P;
  if (key === 'base') {
    const kind = value as BaseKind;
    q.base = kind;
    if (kind === 'plinth') { if (!(q.plinth && q.plinth > 0)) q.plinth = PLINTH.default; }
    else q.plinth = 0;
    if (kind !== 'feet') delete q.feetHeight; // feet take their kind's height (levelling or turned) until set
    if (kind !== 'sides') delete q.toeHeight;
    return q;
  }
  if (key === 'plinth') {
    const v = Math.max(0, Math.round(Number(value)));
    q.plinth = v;
    q.base = v > 0 ? 'plinth' : (q.base === 'feet' || q.base === 'sides' ? q.base : 'none');
    return q;
  }
  (q as Record<string, unknown>)[key] = typeof value === 'number' ? Math.round(value) : value;
  return q;
}
